import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ITransaction} from '../../../shared/interfaces/transaction.interface';
import {ActivityTypesEnum} from '../../../shared/interfaces/ActivityTypesEnum';
import {Select, Store} from '@ngxs/store';
import {
  AmendTransaction,
  GetAllTransactionsWithoutDetails,
  GetTransaction,
  SetDashboardFilters,
  SetTransaction,
  SetTransactionsForScroll,
  SetWidgetData,
} from '../../../shared/states/transactions/transactions.actions';
import {BadgeColorsEnum} from '../../../shared/interfaces/BadgeColorsEnum';
import {Observable} from 'rxjs/internal/Observable';
import {TransactionsState} from '../../../shared/states/transactions/transactions.state';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {getDateFromISO} from '../../../shared/helpers/helpers';
import {SearchInputColorEnum} from '../../../shared/components/search-input/search-input.component';
import {ProfileState} from '../../../shared/states/profile/profile.state';
import {ITeamRecord} from '../../../team/interfaces/team-record.interface';
import {GetUserTeamList} from '../../../shared/states/profile/profile.actions';
import {IOptionItem} from '../../../shared/interfaces/option-item.interface';
import {IAlertMessage} from '../../../shared/interfaces/alert-message.interface';
import {AlertTypesEnum} from '../../../shared/interfaces/AlertTypesEnum';
import {BaseTableDirective} from '../../../shared/directives/base-table.directive';
import {SearchDateRange} from 'src/app/shared/interfaces/SearchDateRange';
import {environment} from 'src/environments/environment';
import {TransactionsService} from '../../../shared/services/transactions.service';
import {FormattedDatePipe} from '../../../shared/pipes/formatted-date/formatted-date.pipe';
import {fadeInOut} from '../../../shared/animations/animations';
import {TRANSACTION_TABLE_PERIODS_LIST} from '../../../shared/constants/search.constants';
import {UserStatusEnum} from '../../../shared/interfaces/UserStatusEnum';
import {IAmendTransaction} from 'src/app/shared/interfaces/amend-transaction.interface';
import { take, withLatestFrom} from 'rxjs/operators';
import { IPendingTransaction } from 'src/app/shared/interfaces/pending-transaction.interface';
import { OrderByEnum } from 'src/app/shared/interfaces/OrderByEnums';
import { ITransactionModel } from 'src/app/shared/interfaces/ITransactionModel';
import { Router } from '@angular/router';
import { SideWidgetService } from 'src/app/shared/services/side-widget.service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { OrderTypeEnum } from 'src/app/shared/interfaces/OrderTypeEnum';
import { OrdersService } from 'src/app/inventory/components/receive-edi/orders.service';
import { ActivitySelectListContent } from 'src/app/shared/interfaces/activity-select-list-content';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';

@UntilDestroy()
@Component({
  selector: 'app-transactions-table',
  templateUrl: './transactions-table.component.html',
  styleUrls: ['./transactions-table.component.scss'],
  animations: [fadeInOut],
})
export class TransactionsTableComponent extends BaseTableDirective implements OnInit {

  transactionsControlsForm: UntypedFormGroup;
  transactionToAmend: ITransaction;
  amendment: IAmendTransaction;
  showPinDialog: boolean;
  showWitnessPinDialog: boolean;
  alertMessage: IAlertMessage;
  autohideDelay = 3;

  shouldRestoreScrollPositionAfterNavigatingBack: boolean;

  showResetPinDialog: boolean;

  @ViewChild('pinUser') pinUserDialog;
  @ViewChild('pinWitness') pinWitnessDialog;
  @ViewChild('tbody') tbody;

  periodsList = TRANSACTION_TABLE_PERIODS_LIST();

  activityTypesList = [...ActivitySelectListContent];

  BadgeColorsEnum = BadgeColorsEnum;
  ActivityTypesEnum = ActivityTypesEnum;
  SearchInputColorEnum = SearchInputColorEnum;
  OrderByEnum = OrderByEnum;

  usersList: IOptionItem[] = [];

  currentPinDialog: 'sign' | 'witness';

  tableColumns = {
    transactionDate: false,
    friendlyName: true,
    rxNumber: true,
    activityType: true,
    startingQuantity: true,
    addedQuantity: true,
    endingQuantity: true,
    audit: true
  };

  showAmendingDialog = false;

  showTransactionMessage = false;
  transactionMessage: IAlertMessage;
  AlertTypesEnum = AlertTypesEnum;

  focusPin: boolean;

  currentDrugId: string;
  dashboardWithCarousel: boolean;

  getDateFromISO = getDateFromISO;

  @Select(TransactionsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(TransactionsState.transactionsForScroll) transactions$: Observable<ITransactionModel[]>;
  @Select(TransactionsState.currentTransaction) widgetTransaction$: Observable<ITransaction[]>;
  @Select(ProfileState.teamList) teamList$: Observable<ITeamRecord[]>;
  @Select(TransactionsState.success) success$: Observable<boolean>;
  @Select(TransactionsState.pendingTransactions) pendingTransactions$: Observable<IPendingTransaction[]>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  environment = environment;
  navigation;

  constructor(
    private fb: UntypedFormBuilder,
    protected store: Store,
    private transactionsService: TransactionsService,
    private formattedDatePipe: FormattedDatePipe,
    private router: Router,
    private sideWidgetService: SideWidgetService,
    private ordersService: OrdersService,
    private alertService: AlertMessageService
  ) {
    super(store);
    this.transactionsControlsForm = this.fb.group({
      periodId: [SearchDateRange.Last30Days, []],
      searchValue: ['', []],
      activityType: [null, []],
      createById: [null, []],
      orderBy: [OrderByEnum.TransactionDate],
      orderByAscending: [false],
      scrollTop: 0,
      schedules: null,
      inventoryLocations: null
    });
    this.dashboardWithCarousel = !!this.store.selectSnapshot(TransactionsState.pendingTransactions)?.length;
    this.loadData = this.filterTableData.bind(this);
    this.navigation = this.router.getCurrentNavigation();
    
    if (!this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.activityTypesList = this.activityTypesList.filter(x => x.id !== ActivityTypesEnum.MovedFrom && x.id !== ActivityTypesEnum.MovedTo);
    }

    if (!this.store.selectSnapshot(PharmacyState.compoundingEnabled)) { 
      this.activityTypesList = this.activityTypesList.filter(x => x.id !== ActivityTypesEnum.Mixed);
    }
  }

  ngOnInit(): void {
    this.store.dispatch(new GetUserTeamList(UserStatusEnum.Active.toString()));
    this.transactionsControlsForm.get('period')?.patchValue({id: SearchDateRange.Last90Days, name: 'Last 90 days'});
    if (this.navigation?.extras?.state?.isNavigatedBack) {
      this.shouldRestoreScrollPositionAfterNavigatingBack = true;
    }
    this.restoreTableFilters();

    this.store.dispatch(new SetWidgetData(null));
    this.store.dispatch(new SetTransaction(null));

    this.teamList$
      .pipe(untilDestroyed(this))
      .subscribe((users: ITeamRecord[]) => {
        if (users) {
          this.usersList = users.map(el => ({id: el.id, name: el.userName}));
          this.usersList.unshift({id: null, name: 'All Users'});
        }
      });

    this.transactionsService.transactionSuccessfullyAmended$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
       this.refreshSideWidgetWithUnderlyingTransactionsTable();
      });

    //refresh underlying transactions 
    this.transactionsService.transactionDateSuccessfullyUpdated$
    .pipe(untilDestroyed(this))
    .subscribe(() => this.refreshAllPages());

    this.pendingTransactions$
    .pipe(untilDestroyed(this))
    .subscribe((resp: any) => { this.dashboardWithCarousel = !!resp?.length });

    this.transactionsService.refreshTransactionsTable$
    .pipe(untilDestroyed(this))
    .subscribe(() => { 
     this.refreshAllPages();
    });

    this.transactionsControlsForm.valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(filters => {
      this.store.dispatch(new SetDashboardFilters(filters));
    })
    
    this.transactionsControlsForm?.get('schedules')?.valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(() => this.fetchFirstPage());

    this.transactionsControlsForm?.get('inventoryLocations')?.valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(() => this.fetchFirstPage());
  }

  getDateString(ISO: string): string {
    const date = new Date(Date.parse(ISO));
    return this.formattedDatePipe.transform(date, 'MMM dd, hh:mm');
  }

  searchCleared(): void {
    this.currentDrugId = null;
  }

  optionSelected(item: any): void {
    this.currentDrugId = item.drugID;
    this.filterTableData();
  }

  filterTableData(): void {
    const form = this.transactionsControlsForm.getRawValue();
    const criteria = this.transactionsService.transactionsFilter;
    criteria.page = this.currentPage;
    criteria.pageSize = this.pageSize;
    criteria.drugId = this.currentDrugId;
    criteria.activityType = form.activityType;
    criteria.searchDateRange = form.periodId || SearchDateRange.All;
    criteria.searchTerms = form.searchValue;
    criteria.createById = form.createById;
    criteria.offset = new Date().getTimezoneOffset() / 60,
    criteria.orderBy = form.orderBy;
    criteria.orderByAscending = form.orderByAscending;
    criteria.schedules = form?.schedules?.map(x => x.id);
    criteria.inventoryLocations = form?.inventoryLocations?.map(x => x.id);

    this.store.dispatch(new GetAllTransactionsWithoutDetails(criteria))
    .pipe(untilDestroyed(this))
    .subscribe(_ => {
      if (criteria?.page === 0) {
        this.store.dispatch(new SetTransactionsForScroll([]));
      }
      const loaded = this.store.selectSnapshot(TransactionsState)?.currentPageTransactions;
        this.store.dispatch(new SetTransactionsForScroll([...this.store.selectSnapshot(TransactionsState)?.transactionsForScroll, ...loaded]));
    });
  }

  refreshAllPages(): void {
    const criteria = {...this.transactionsService.transactionsFilter};
    criteria.pageSize *= (this.currentPage + 1);
    criteria.page = 0;
    this.store.dispatch(new GetAllTransactionsWithoutDetails(criteria))
    .pipe(untilDestroyed(this))
    .subscribe(_ => {
      const loaded = this.store.selectSnapshot(TransactionsState)?.currentPageTransactions;
      this.store.dispatch(new SetTransactionsForScroll(loaded));
    });
  }

  private refreshSideWidgetWithUnderlyingTransactionsTable() {
    this.refreshAllPages();
  }

  amended(amendment): void {
    this.amendment = amendment;
    this.showPinDialog = true;
    setTimeout(() => this.focusPin = true);
    this.currentPinDialog = 'sign';
  }

  closePinDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
  }

  afterPinHandled({success, pin}): void {
    if (success) {
      this.amendment.userPin = parseInt(pin, 10);
      this.closePinDialog();
      this.showWitnessPinDialog = true;
      setTimeout(() => this.focusPin = true);
      this.currentPinDialog = 'witness';
    }
  }

  closeWitnessPinDialog(): void {
    this.showWitnessPinDialog = false;
    this.focusPin = false;
  }

  afterWitnessPinHandled({success, pin}): void {
    if (success) {
      this.amendment.witnessPin = parseInt(this.pinWitnessDialog.pinValue, 10);

      this.closeWitnessPinDialog();
      this.amendTransaction();
    }
  }

  openResetPinDialog(): void {
    this.focusPin = false;
    this.showPinDialog = false;
    this.showWitnessPinDialog = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;

    if (this.currentPinDialog === 'sign') {
      this.showPinDialog = true;
    } else {
      this.showWitnessPinDialog = true;
    }

    setTimeout(() => this.focusPin = true);
  }

  afterPinUpdated(success: boolean): void {
    // TODO: show message about pin update
    if (success) {
      this.closeResetPinDialog();
    }
  }

  amendTransaction(): void {
    this.showAmendingDialog = false;
    this.store.dispatch(new AmendTransaction(this.amendment, this.transactionToAmend.id, this.transactionToAmend.drug.friendlyName, this.transactionToAmend.drug.packageNdc))
      .pipe(take(1), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        if (success) {
          this.alertService.displayAlert('Successfully amended.', AlertTypesEnum.Success);
        } else {
          this.alertService.displayAlert('Amendment failed.', AlertTypesEnum.Error);
        }

        if (success) {
          this.refreshAllPages();
        }
      });
  }

  cancelAmend(): void {
    this.showAmendingDialog = false;
  }

  openSideWidget(transaction: ITransactionModel): void {
    this.sideWidgetService.openSideWidget(transaction.id);
  }

  sortByColumn(orderBy: OrderByEnum): void {
    switch (orderBy) {
      case OrderByEnum.TransactionDate:
        this.tableColumns.transactionDate = !this.tableColumns.transactionDate;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.transactionDate);
        break;
      case OrderByEnum.FriendlyName:
        this.tableColumns.friendlyName = !this.tableColumns.friendlyName;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.friendlyName);
        break;
      case OrderByEnum.RxNumber:
        this.tableColumns.rxNumber = !this.tableColumns.rxNumber;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.rxNumber);
        break;
      case OrderByEnum.ActivityType:
        this.tableColumns.activityType = !this.tableColumns.activityType;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.activityType);
        break;
      case OrderByEnum.StartingQuantity:
        this.tableColumns.startingQuantity = !this.tableColumns.startingQuantity;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.startingQuantity);
        break;
      case OrderByEnum.AddedQuantity:
        this.tableColumns.addedQuantity = !this.tableColumns.addedQuantity;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.addedQuantity);
        break;
      case OrderByEnum.EndingQuantity:
        this.tableColumns.endingQuantity = !this.tableColumns.endingQuantity;
        this.transactionsControlsForm.get('orderByAscending').setValue(this.tableColumns.endingQuantity);
        break;
      default:
        break;
    }
    this.transactionsControlsForm.get('orderBy').setValue(orderBy);
    this.fetchFirstPage();
  }

  fetchFirstPage(): void {
    this.forceScrollUp(this.tbody?.nativeElement);
    this.resetPagingValues();
    this.filterTableData();
  }

  searchChanged(): void {
    if (!this.shouldRestoreScrollPositionAfterNavigatingBack || !this.store.selectSnapshot(TransactionsState)?.transactionsForScroll?.length) {
      this.fetchFirstPage();
    } else {
      this.shouldRestoreScrollPositionAfterNavigatingBack = false;
    }
  }

  openAuditOrOrder(transaction: ITransactionModel): void {
    this.saveScrollPosition(this.tbody?.nativeElement?.scrollTop);
    if (transaction?.auditId) {
        event.stopImmediatePropagation();
        this.router.navigate(['audits/details', transaction.auditId], {state : {
          navigatedFrom: this.router.url 
        }
      });
    } else if (transaction?.orderId) {
      event.stopImmediatePropagation();
      this.ordersService.navigateToOrderDetails(this.router, transaction?.orderType, transaction.orderId);
    } else if (transaction?.activityType === ActivityTypesEnum.Compounded) {
      this.router.navigate(['inventory/compounds', transaction.id], {state : {
        navigatedFrom: this.router.url 
      }});
    }
  }

  restoreTableFilters(): void {
    const filters = this.store.selectSnapshot(TransactionsState)?.dashboardFilters;
    this.transactionsControlsForm.patchValue(filters, {emitEvent: false});
    setTimeout(() => {
      this.tbody?.nativeElement?.scrollTo(0, filters?.scrollTop);
    });
    setTimeout(() => {
      this.saveScrollPosition(0);
    });
  }

  private saveScrollPosition(scrollTop: number) {
    this.transactionsControlsForm.get('scrollTop').setValue(scrollTop);
  }
}